import { useEffect, useRef, useState } from "react";
import { useMutation } from "@apollo/client/react";
import { gql } from "graphql-tag";
import { toast } from "sonner";

export function setsEqual<T>(a: Set<T>, b: Set<T>): boolean {
    if (a.size !== b.size) return false;
    for (const v of a) if (!b.has(v)) return false;
    return true;
}

export const SET_SOURCE_SELECTED_MUTATION = gql`
    mutation SetSourceSelected(
        $messageSourceId: UUID!
        $selected: Boolean!
        $feedbackReason: String
        $feedbackText: String
    ) {
        messageSetSourceSelected(
            messageSourceId: $messageSourceId
            selected: $selected
            feedbackReason: $feedbackReason
            feedbackText: $feedbackText
        )
    }
`;

interface UseSourceSelectionOptions {
    messageId: string | null | undefined;
    /**
     * Current source pool for this message. Each entry's `selected` flag is the
     * server's source of truth; used to seed initial deselection state so a page
     * reload restores deselected sources. Ignored on subsequent refetches to
     * avoid clobbering session-local toggles that have not round-tripped yet.
     */
    sources?: ReadonlyArray<{ id: string; selected?: boolean | null }> | null;
    /** Called after a successful toggle; consumers typically refetch. */
    onChanged?: () => void;
}

export interface UseSourceSelectionReturn {
    /** Source ids deselected (server + this session). */
    deselectedSourceIds: Set<string>;
    /**
     * The server-seeded baseline of deselected source ids at the time this
     * message was first loaded (or navigated back to). Stable for a given
     * messageId. Use to detect whether session toggles have returned to the
     * original state.
     */
    baselineDeselectedSourceIds: Set<string>;
    /**
     * Number of user-initiated source toggles since the current message was loaded.
     * Resets to 0 on messageId change. Use this (not deselectedSourceIds.size) for
     * eager-generation triggers so server-seeded initial state never fires a regen.
     */
    userToggleCount: number;
    /**
     * Toggle a source's selection by its stable id. Persists via
     * messageSetSourceSelected. Deselecting excludes the source from the next
     * generation run but keeps it visible on the panel.
     *
     * Internally detects whether the toggle returns the selection to the server
     * baseline and skips the `userToggleCount` increment in that case, so
     * eager-generation is not re-triggered for an undo round-trip. Detection
     * uses a ref so it is always based on the latest state even inside stale
     * closures.
     */
    toggleSource: (sourceId: string) => void;
}

/**
 * Persists source selection via messageSetSourceSelected, keyed on the stable
 * MessageSource id (not a volatile citation index). Seeds initial state from
 * the source pool's `selected` flags and keeps a per-message session cache so
 * navigating A→B→A restores A's local toggles.
 */
export function useSourceSelection({
    messageId,
    sources,
    onChanged,
}: UseSourceSelectionOptions): UseSourceSelectionReturn {
    const [deselected, setDeselected] = useState<Set<string>>(new Set());
    const [baseline, setBaseline] = useState<Set<string>>(new Set());
    const [userToggleCount, setUserToggleCount] = useState(0);

    const deselectedRef = useRef(deselected);
    deselectedRef.current = deselected;
    const baselineRef = useRef(baseline);
    baselineRef.current = baseline;

    const deselectedByMessage = useRef<Map<string, Set<string>>>(new Map());
    const baselineByMessage = useRef<Map<string, Set<string>>>(new Map());
    const initializedRef = useRef(false);
    const prevMessageIdRef = useRef<string | null | undefined>(undefined);

    useEffect(() => {
        const prevId = prevMessageIdRef.current;

        if (messageId !== prevId) {
            if (prevId != null) {
                deselectedByMessage.current.set(prevId, new Set(deselectedRef.current));
            }
            prevMessageIdRef.current = messageId;
            initializedRef.current = false;
        }

        if (!messageId) {
            // Idempotent reset. This branch runs on every effect pass while there
            // is no message, and the effect re-runs whenever the `sources` prop
            // identity changes — which some callers recreate on every render. A
            // fresh `new Set()` is a new reference each time, so an unconditional
            // setState here would re-render → re-run this effect → setState again,
            // an unbounded loop ("Maximum update depth exceeded"). Returning the
            // previous value when already empty lets React bail out of the update.
            setDeselected((prev) => (prev.size === 0 ? prev : new Set()));
            setBaseline((prev) => (prev.size === 0 ? prev : new Set()));
            setUserToggleCount((c) => (c === 0 ? c : 0));
            return;
        }

        if (initializedRef.current) return;

        const cached = deselectedByMessage.current.get(messageId);
        if (cached !== undefined) {
            setDeselected(new Set(cached));
            const cachedBaseline = baselineByMessage.current.get(messageId) ?? new Set(cached);
            setBaseline(new Set(cachedBaseline));
            setUserToggleCount(0);
            initializedRef.current = true;
            return;
        }

        if (sources != null) {
            const s = new Set<string>();
            for (const src of sources) {
                if (src.selected === false) s.add(src.id);
            }
            setDeselected(s);
            setBaseline(new Set(s));
            baselineByMessage.current.set(messageId, new Set(s));
            initializedRef.current = true;
        }
    }, [messageId, sources]);

    const [setSelectedMutation] = useMutation<{ messageSetSourceSelected: boolean }>(
        SET_SOURCE_SELECTED_MUTATION,
        {
            onError: (error) =>
                toast.error(`Quelle konnte nicht aktualisiert werden: ${error.message}`),
            onCompleted: () => onChanged?.(),
        },
    );

    const toggleSource = (sourceId: string) => {
        if (!messageId) return;
        // Use refs so this is always fresh even inside stale closures.
        const current = deselectedRef.current;
        const base = baselineRef.current;
        const wasDeselected = current.has(sourceId);
        const next = new Set(current);
        if (wasDeselected) next.delete(sourceId); else next.add(sourceId);
        // Only count as a user toggle when NOT reverting to the server baseline.
        const isRevert = setsEqual(next, base);
        if (!isRevert) setUserToggleCount((c) => c + 1);
        setDeselected(next);
        void setSelectedMutation({
            variables: { messageSourceId: sourceId, selected: wasDeselected },
        });
    };

    return { deselectedSourceIds: deselected, baselineDeselectedSourceIds: baseline, userToggleCount, toggleSource };
}
